/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import { ImageSlideshowElement } from "../basic/image-slideshow-element";
import { StoryElement, ImageGalleryElement } from "@quintype/components";
import { Text } from ".././../../arrow-components/src/components/Atoms/StoryElements/Text";
import { Image } from ".././../../arrow-components/src/components/Atoms/StoryElements/Image";
import { Blurb } from ".././../../arrow-components/src/components/Atoms/StoryElements/Blurb";
import { Summary } from ".././../../arrow-components/src/components/Atoms/StoryElements/Summary";
import { Attachment } from ".././../../arrow-components/src/components/Atoms/StoryElements/Attachment";
import { QuestionAnswer } from ".././../../arrow-components/src/components/Atoms/StoryElements/QuestionAnswer";
import { BlockQuote } from ".././../../arrow-components/src/components/Atoms/StoryElements/BlockQuote";
import { AlsoRead } from ".././../../arrow-components/src/components/Atoms/StoryElements/AlsoRead";
import { Quote } from ".././../../arrow-components/src/components/Atoms/StoryElements/Quote";
import { BigFact } from ".././../../arrow-components/src/components/Atoms/StoryElements/BigFact";
import { ImageLightbox } from "./lightbox/image-lightbox";
import { Reference } from ".././../../arrow-components/src/components/Atoms/StoryElements/Reference";
import get from "lodash/get";
import PropTypes from "prop-types";
import { PromotionalMessageElement } from "../basic/promotional-message-element";
import { getSubType, getInfiniteScrollConfigStyle } from "../../utils.js";
import { truncate } from "../../../utils/truncate-string";
import { CustomLink } from "../custom-link";
import {
  BlurbTemplatesMapping,
  BlockQuoteTemplatesMapping,
  BlockQuoteIconType,
  SummaryTemplatesMapping,
  QATemplatesMapping,
  QuestionTemplatesMapping,
  AnswerTemplatesMapping,
  AlsoReadTemplatesMapping,
  QuoteTemplatesMapping
} from "./story-template-map";
import "./story-page-card.module.css";

const isFirstWhiteListed = (index, key) => {
  const whiteListObject = {
    0: {
      "also-read": true,
      quote: true,
      blockquote: true
    }
  };

  return get(whiteListObject, [index, key], false);
};

const StoryPageCard = props => {
  const elementAlignment = get(props, ["card", "metadata", "attributes", "elementalignment", 0], "").toLowerCase();
  const pageBuilderStoryElementSetting = get(
    props,
    ["config", "pagebuilder-config", "story", "globalStoryElements"],
    {}
  );

  function getElement(element, index, props) {
    const subType = getSubType(element);
    const primaryColor = get(props, ["config", "theme-attributes", "primary_color"]);

    switch (subType) {
      case "summary":
        const summaryTemplate = get(props, ["config", "theme-attributes", "story-elements", "summary"], "");
        const {
          template: summaryPBTemplate = "",
          css: { headerBgColor = "" } = {},
          opts: { headline = "", hideHeadline = "" } = {}
        } = get(pageBuilderStoryElementSetting, ["summary"], {});
        return (
          <Summary
            element={element}
            template={summaryPBTemplate || SummaryTemplatesMapping[summaryTemplate]}
            opts={{
              headline: headline || "Summary",
              hideHeadline: hideHeadline
            }}
            css={{ headerBgColor: headerBgColor || primaryColor }}
          />
        );
      case "blurb":
        const blurbTemplate = get(props, ["config", "theme-attributes", "story-elements", "blurb"], "");
        const { template: blurbPBTemplate = "", css: { borderColor = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["blurb"],
          {}
        );
        return (
          <Blurb
            element={element}
            template={blurbPBTemplate || BlurbTemplatesMapping[blurbTemplate]}
            css={{ borderColor: borderColor || primaryColor }}
          />
        );
      case "blockquote":
        const blockQuoteTemplate = get(props, ["config", "theme-attributes", "story-elements", "block-quote"], "");
        const {
          template: blockQuotePBTemplate = "",
          css: { iconType = "", blockQuoteColor = "", backgroundShade = "" } = {}
        } = get(pageBuilderStoryElementSetting, ["blockquote"], {});
        return (
          <BlockQuote
            element={element}
            template={blockQuotePBTemplate || BlockQuoteTemplatesMapping[blockQuoteTemplate]}
            css={{
              iconType: iconType || BlockQuoteIconType[blockQuoteTemplate],
              blockQuoteColor: blockQuoteColor || primaryColor,
              backgroundShade: backgroundShade || primaryColor
            }}
          />
        );
      case "bigfact":
        return <BigFact element={element} />;
      case "quote":
        const quoteTemplate = get(props, ["config", "theme-attributes", "quote-element"], "default");
        const { template: quotePBTemplate = "", css: { borderColor: quoteBorderColor = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["quote"],
          {}
        );
        return (
          <Quote
            element={element}
            template={quotePBTemplate || QuoteTemplatesMapping[quoteTemplate]}
            css={{ borderColor: quoteBorderColor || primaryColor }}
          />
        );
      case "image-gallery":
        const template = get(element, ["metadata", "type"]) === "gallery" ? ImageGalleryElement : ImageSlideshowElement;
        return (
          <div>
            <ImageLightbox template={template} element={element} story={props.story} cdn={props.cdn} />
          </div>
        );
      case "also-read":
        const alsoReadTemplate = get(props, ["config", "theme-attributes", "also-read-element"], "");
        const { template: alsoReadPBTemplate = "", css: { textColor = "" } = {}, opts: { title = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["also-read"],
          {}
        );
        return (
          <AlsoRead
            story={props.story}
            element={element}
            template={alsoReadPBTemplate || AlsoReadTemplatesMapping[alsoReadTemplate]}
            css={{ textColor: textColor || primaryColor }}
            opts={{ title }}
          />
        );
      case "promotional-message":
        return <PromotionalMessageElement element={element} externalLink />;
      case "image":
        return <Image element={element} />;
      case "q-and-a":
        const questionAnswerTemplate = get(
          props,
          ["config", "theme-attributes", "story-elements", "q-and-a-element"],
          "q-and-a-element-default"
        );
        return (
          <QuestionAnswer
            element={element}
            opts={{
              type: "q-and-a",
              defaultIconType: "edge"
            }}
            template={QATemplatesMapping[questionAnswerTemplate]}
            css={{
              iconColor: primaryColor
            }}
          />
        );
      case "question":
        const questionTemplate = get(
          props,
          ["config", "theme-attributes", "story-elements", "question-element"],
          "question-element-default"
        );
        return (
          <QuestionAnswer
            element={element}
            opts={{
              type: "question",
              defaultIconType: "edge"
            }}
            template={QuestionTemplatesMapping[questionTemplate]}
            css={{
              iconColor: primaryColor
            }}
          />
        );
      case "answer":
        const answerTemplate = get(
          props,
          ["config", "theme-attributes", "story-elements", "answer-element"],
          "answer-element-default"
        );
        return (
          <QuestionAnswer
            element={element}
            opts={{
              type: "answer",
              defaultIconType: "edge"
            }}
            template={AnswerTemplatesMapping[answerTemplate]}
            css={{
              iconColor: primaryColor
            }}
          />
        );
      case "attachment":
        return <Attachment element={element} />;
      case "text":
        const { css: { textColor: textElementColor = "", hyperlinkColor = "" } = {} } = get(
          pageBuilderStoryElementSetting,
          ["text"],
          {}
        );
        return <Text element={element} css={{ textColor: textElementColor, hyperlinkColor: hyperlinkColor }} />;
      case "references":
        const { opts: { headlineText = "", showHeadline = true } = {} } = get(
          pageBuilderStoryElementSetting,
          ["references"],
          {}
        );
        return <Reference element={element} opts={{ showHeadline: showHeadline, headlineText: headlineText }} />;
      default:
        return <StoryElement element={element} story={props.story} />;
    }
  }

  const firstElement = get(props, ["card", "story-elements", 0]);
  const infiniteScrollStyle = getInfiniteScrollConfigStyle(props.config);
  const cardStyle =
    elementAlignment && isFirstWhiteListed(0, firstElement.subtype) ? `card--${elementAlignment}` : "card-grid";
  const mountAt = get(props, ["config", "mountAt"]) || "";
  if (infiniteScrollStyle === "read-more" && !props.firstStory) {
    return (
      <div data-testid="story-card" className={`card ${cardStyle} ${props.clazzName}`}>
        {(firstElement.type === "text" || firstElement.type === "title") && (
          <div key={firstElement.id} className={`read-more-card element-wrapper read-more-overlay`}>
            <bdi dangerouslySetInnerHTML={{ __html: truncate(firstElement.text, 400, true) }} />
            <div className="content-overlay" />
          </div>
        )}

        <div className={`read-more-btn card element-wrapper `}>
          <CustomLink href={`${mountAt}/${get(props, ["story", "slug"], "")}`}>Read More</CustomLink>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="story-card" className={`card  ${cardStyle} ${props.clazzName}`}>
      {props.card["story-elements"]
        .filter(element => element.id !== props.firstVideoElementID)
        .map((element, index) => {
          const removeMaxWidth = elementAlignment && isFirstWhiteListed(index, element.subtype) ? "remove-margin" : "";
          return (
            <div key={element.id} className={`element-wrapper ${removeMaxWidth}`}>
              {getElement(element, index, props)}
            </div>
          );
        })}
    </div>
  );
};

StoryPageCard.propTypes = {
  firstStory: PropTypes.bool,
  card: PropTypes.object,
  firstVideoElementID: PropTypes.string,
  story: PropTypes.object,
  cdn: PropTypes.string,
  clazzName: PropTypes.string,
  config: PropTypes.object
};

export { StoryPageCard };
